import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import useNav from "./hooks/useNav";
import { Clock, CheckCircle, Home, MapPin, ClipboardList } from "lucide-react";
import { useAuthFetch } from "./hooks/useAuthFetch";
import { useCart } from "./context/CartContext";
import { useRestaurant } from "./context/RestaurantContext";
import appConfig from "./config";
import { toast } from "react-toastify";
import PrimaryButton from "./components/PrimaryButton";

const POLL_INTERVAL = 4000;

const OrderWaitingPage = () => {
    const navigate = useNav();
    const location = useLocation();
    const { authFetch } = useAuthFetch();
    const { clearCart } = useCart();

    const { order, paymentMethod } = location.state || {};
    const { restaurant } = useRestaurant();

    const [dots, setDots] = useState(".");
    const [orderDetails, setOrderDetails] = useState(null);
    const [confirmed, setConfirmed] = useState(false);
    const intervalRef = useRef(null);
    const pollRef = useRef(null);
    const isMounted = useRef(true);

    useEffect(() => {
        if (!order) {
            toast.error("Данные заказа не найдены");
            navigate("/");
            return;
        }

        clearCart();

        authFetch(`${appConfig.endpoint}/orders/${order.id}/view`)
            .then(data => {
                if (data?.success && isMounted.current) setOrderDetails(data.order);
            })
            .catch(() => {});

        intervalRef.current = setInterval(() => {
            setDots(d => d.length >= 3 ? "." : d + ".");
        }, 600);

        const poll = async () => {
            try {
                const data = await authFetch(`${appConfig.endpoint}/orders/${order.id}/status`);
                if (!isMounted.current) return;

                const status = data?.status;

                if (status === 1) {
                    clearInterval(pollRef.current);
                    clearInterval(intervalRef.current);
                    if (paymentMethod === "online") {
                        navigate("/payment", { state: { order } });
                    } else {
                        setConfirmed(true);
                    }
                }

                if (status === 5) {
                    clearInterval(pollRef.current);
                    clearInterval(intervalRef.current);
                    toast.error("Заказ отменён рестораном");
                    navigate("/");
                }
            } catch {
                // ignore network errors — keep polling
            }
        };

        poll();
        pollRef.current = setInterval(poll, POLL_INTERVAL);

        return () => {
            isMounted.current = false;
            clearInterval(intervalRef.current);
            clearInterval(pollRef.current);
        };
    }, []);

    if (!order) return null;

    const orderInfo = orderDetails || order;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-screen-sm mx-auto px-4 pt-10 pb-32">

                {/* Header */}
                <div className="flex flex-col items-center text-center mb-8">
                    {restaurant?.logo ? (
                        <div className="relative mb-5">
                            <div className="absolute inset-0 -m-4 rounded-full bg-brandBlue/8" />
                            <img
                                src={restaurant.logo}
                                alt={restaurant.name}
                                className="relative h-24 w-auto max-w-[180px] object-contain"
                                style={{ filter: 'drop-shadow(0 4px 16px rgba(62,125,174,0.2))' }}
                                onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
                            />
                        </div>
                    ) : (
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center mb-4"
                            style={{ background: confirmed ? "rgb(240 253 244)" : "rgba(59,130,246,0.08)" }}
                        >
                            {confirmed
                                ? <CheckCircle size={40} className="text-green-500" strokeWidth={1.5} />
                                : <Clock size={40} className="text-brandBlue animate-pulse" strokeWidth={1.5} />
                            }
                        </div>
                    )}
                    <h1 className="font-playfair text-2xl font-semibold text-gray-900 tracking-wide">
                        {restaurant?.name || "Ваш заказ"}
                    </h1>
                    <div className="mt-2 mb-3 w-10 h-0.5 rounded-full bg-brandBlue/50" />
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-2 ${confirmed ? "bg-green-50 text-green-600" : "bg-brandBlue/8 text-brandBlue"}`}>
                        {confirmed
                            ? <CheckCircle size={12} strokeWidth={2.5} />
                            : <Clock size={12} className="animate-pulse" strokeWidth={2.5} />
                        }
                        {confirmed ? "Заказ принят!" : `Ожидаем подтверждения${dots}`}
                    </span>
                    <p className="text-gray-400 text-sm">
                        Заказ <span className="font-mono font-semibold text-gray-600">#{orderInfo.id}</span>
                    </p>
                </div>

                {/* Summary card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div className="bg-brandBlue/5 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Сумма</p>
                            <p className="text-xl font-bold text-brandBlue">{orderInfo.amount} ₽</p>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3.5">
                            <p className="text-xs text-gray-500 mb-1">Оплата</p>
                            <p className="text-base font-semibold text-gray-800">
                                {paymentMethod === "cash" ? "Наличными" : "Онлайн"}
                            </p>
                            {paymentMethod === "cash" && (
                                <p className="text-xs text-gray-400 mt-0.5">При получении</p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                            <MapPin size={16} className="text-gray-400" />
                        </div>
                        <div>
                            <p className="text-sm font-medium text-gray-800">Получение</p>
                            <p className="text-sm text-gray-500">
                                {orderInfo.delivery ? "Доставка" : "Самовывоз из ресторана"}
                            </p>
                        </div>
                    </div>

                    {orderInfo.note && (
                        <div className="pt-3 mt-3 border-t border-gray-100">
                            <p className="text-xs text-gray-400 mb-1">Комментарий</p>
                            <p className="text-sm text-gray-600">{orderInfo.note}</p>
                        </div>
                    )}
                </div>

                {/* Status steps */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-4">
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <CheckCircle size={18} className="text-green-500 shrink-0" />
                            <p className="text-sm text-gray-700">Заказ успешно создан</p>
                        </div>
                        <div className="flex items-center gap-3">
                            {confirmed
                                ? <CheckCircle size={18} className="text-green-500 shrink-0" />
                                : <Clock size={18} className="text-brandBlue shrink-0 animate-pulse" />
                            }
                            <p className="text-sm text-gray-700">
                                {confirmed ? "Подтверждён рестораном" : "Ожидаем подтверждения от ресторана"}
                            </p>
                        </div>
                        {confirmed && (
                            <div className="flex items-center gap-3">
                                <Clock size={18} className="text-gray-300 shrink-0" />
                                <p className="text-sm text-gray-500">
                                    {orderInfo.delivery
                                        ? "Курьер свяжется с вами для уточнения деталей"
                                        : "Подойдите к стойке и назовите номер заказа"}
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {!confirmed && (
                    <p className="text-xs text-gray-400 text-center">
                        Страницу можно закрыть — вы получите уведомление в Telegram
                    </p>
                )}
            </div>

            {/* Fixed bottom */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 px-4 py-4 shadow-lg">
                <div className="max-w-screen-sm mx-auto flex gap-3">
                    <PrimaryButton
                        onClick={() => navigate("/")}
                        className="flex-1 flex items-center justify-center gap-2 py-3"
                    >
                        <Home size={18} className="shrink-0" />
                        Главная
                    </PrimaryButton>
                    <button
                        onClick={() => navigate("/history")}
                        className="flex-1 flex items-center justify-center gap-2 bg-white border border-gray-200 text-gray-700 py-3 px-4 rounded-2xl font-medium text-sm active:scale-95 transition-all"
                    >
                        <ClipboardList size={18} className="shrink-0" />
                        Мои заказы
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OrderWaitingPage;